/**
 * Notifications API client for the new push-notification system.
 *
 * Backend endpoints (mounted at `/api/notifications` and
 * `/api/portal/events/stream?channel=notifications`):
 *
 *   GET    /api/notifications
 *   GET    /api/notifications/unread-count
 *   PATCH  /api/notifications/:id/read
 *   POST   /api/notifications/mark-all-read
 *   DELETE /api/notifications/:id
 *   POST   /api/notifications/broadcast
 *   GET    /api/notifications/broadcasts
 *   PUT    /api/notifications/preferences/employee/:enrollId
 *   GET    /api/notifications/preferences/me
 *   PUT    /api/notifications/preferences/me
 *
 * Note: the legacy `notificationsApi` in `features/notifications/api.ts` is a
 * separate feature ("Pending Fingerprints"). This new client lives under
 * `features/notifications/api/` to avoid colliding with it.
 */

import { apiClient } from '@lib/api/client';
import { tokenStorage } from '@lib/auth/storage';

// ───────── Types ─────────

export type NotificationType =
  | 'leave_request_submitted'
  | 'leave_pending_approval'
  | 'leave_approved'
  | 'leave_rejected'
  | 'attendance_check_in'
  | 'attendance_check_out'
  | 'attendance_late'
  | 'reward_granted'
  | 'penalty_applied'
  | 'holiday_announced'
  | 'broadcast'
  | 'system';

export interface NotificationDoc {
  _id: string;
  type: NotificationType;
  title_ar: string;
  title_en: string;
  body_ar: string;
  body_en: string;
  data?: {
    entity_type?: string;
    entity_id?: string;
    deep_link?: string;
    extra?: unknown;
  };
  read: boolean;
  read_at?: string | null;
  createdAt: string;
  updatedAt: string;
  fcm?: {
    status?: 'pending' | 'sent' | 'failed' | 'skipped';
    sent_at?: string | null;
    error?: string | null;
    message_id?: string | null;
    tokens_sent_count?: number;
    tokens_failed_count?: number;
  };
  campaign_id?: string | null;
}

export interface NotificationsListParams {
  page?: number;
  limit?: number;
  read?: boolean;
  type?: NotificationType;
}

export interface NotificationsListResponse {
  data: NotificationDoc[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UnreadCountResponse {
  count: number;
}

export type BroadcastAudienceScope = 'all' | 'departments' | 'branches' | 'employees';

export interface BroadcastAudience {
  scope: BroadcastAudienceScope;
  department_ids?: string[];
  branch_ids?: string[];
  enroll_ids?: (string | number)[];
}

export interface BroadcastPayload {
  audience: BroadcastAudience;
  title: { ar: string; en: string };
  body: { ar: string; en: string };
  data?: {
    deep_link?: string;
    extra?: Record<string, unknown>;
  };
  also_save_as_news?: boolean;
}

export interface BroadcastResult {
  campaign_id: string;
  estimated_recipients: number;
  status: 'queued';
}

export interface BroadcastHistoryItem {
  campaign_id: string;
  title_ar?: string;
  title_en?: string;
  body_ar?: string;
  body_en?: string;
  createdAt: string;
  recipients_count: number;
  sent_count: number;
  failed_count: number;
  /** Some backends may return additional aggregates — keep an escape hatch. */
  [key: string]: unknown;
}

export interface BroadcastsHistoryResponse {
  data: BroadcastHistoryItem[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/** Per-employee toggles the *manager* sets to follow that employee's attendance. */
export interface EmployeeNotifPrefs {
  on_check_in?: boolean;
  on_check_out?: boolean;
  on_late_only?: boolean;
}

/** Manager's own preferences (quiet hours, master switches). */
export interface ManagerNotifPrefs {
  receive_leave_requests?: boolean;
  receive_attendance?: boolean;
  quiet_hours?: {
    enabled?: boolean;
    from?: string;
    to?: string;
  };
}

// ───────── Helpers ─────────

function buildListQuery(params: NotificationsListParams): string {
  const q = new URLSearchParams();
  q.set('page', String(params.page ?? 1));
  q.set('limit', String(params.limit ?? 20));
  if (params.read !== undefined) q.set('read', String(params.read));
  if (params.type) q.set('type', params.type);
  return q.toString();
}

// ───────── API ─────────

export const notificationsApi = {
  list: async (params: NotificationsListParams = {}): Promise<NotificationsListResponse> => {
    const { data } = await apiClient.get<NotificationsListResponse>(
      `/api/notifications?${buildListQuery(params)}`,
    );
    return data;
  },

  unreadCount: async (): Promise<UnreadCountResponse> => {
    const { data } = await apiClient.get<UnreadCountResponse>(
      '/api/notifications/unread-count',
    );
    return data;
  },

  markRead: async (id: string): Promise<NotificationDoc> => {
    const { data } = await apiClient.patch<NotificationDoc>(
      `/api/notifications/${id}/read`,
    );
    return data;
  },

  markAllRead: async (): Promise<{ modified: number }> => {
    const { data } = await apiClient.post<{ modified: number }>(
      '/api/notifications/mark-all-read',
    );
    return data;
  },

  deleteOne: async (id: string): Promise<{ success: boolean }> => {
    const { data } = await apiClient.delete<{ success: boolean }>(
      `/api/notifications/${id}`,
    );
    return data;
  },

  broadcast: async (payload: BroadcastPayload): Promise<BroadcastResult> => {
    const { data } = await apiClient.post<BroadcastResult>(
      '/api/notifications/broadcast',
      payload,
    );
    return data;
  },

  listBroadcasts: async (
    page = 1,
    limit = 20,
  ): Promise<BroadcastsHistoryResponse> => {
    const q = new URLSearchParams({ page: String(page), limit: String(limit) });
    const { data } = await apiClient.get<BroadcastsHistoryResponse>(
      `/api/notifications/broadcasts?${q.toString()}`,
    );
    return data;
  },

  setEmployeePrefs: async (
    enrollId: string | number,
    prefs: EmployeeNotifPrefs,
  ): Promise<{ success: boolean }> => {
    const { data } = await apiClient.put<{ success: boolean }>(
      `/api/notifications/preferences/employee/${encodeURIComponent(String(enrollId))}`,
      prefs,
    );
    return data;
  },

  myPrefs: async (): Promise<ManagerNotifPrefs> => {
    const { data } = await apiClient.get<ManagerNotifPrefs>(
      '/api/notifications/preferences/me',
    );
    return data;
  },

  setMyPrefs: async (prefs: ManagerNotifPrefs): Promise<{ success: boolean }> => {
    const { data } = await apiClient.put<{ success: boolean }>(
      '/api/notifications/preferences/me',
      prefs,
    );
    return data;
  },

  /**
   * Build the SSE stream URL for the `notifications` channel. EventSource
   * cannot send Authorization headers so we append the JWT as a query param
   * (same pattern as `portalJobsApi.streamUrl`). Returns null when there is
   * no token in storage — callers feed null straight to `useEventSource`.
   */
  streamUrl(): string | null {
    const token = tokenStorage.getAccessToken();
    if (!token) return null;
    return `/api/portal/events/stream?channel=notifications&token=${encodeURIComponent(token)}`;
  },
};

export type NotificationsApi = typeof notificationsApi;
